import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, Plus, Send, ChevronRight, CheckCircle2, Clock, AlertCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { api, formatDateTimeBR, ROLES } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";

const STATUSES = {
  em_dia: { label: "Em dia", icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  aguardando: { label: "Aguardando", icon: Clock, cls: "bg-amber-50 text-amber-900 border-amber-200" },
  em_atraso: { label: "Em atraso", icon: AlertCircle, cls: "bg-red-50 text-red-800 border-red-200" },
  concluida: { label: "Concluída", icon: CheckCircle2, cls: "bg-slate-100 text-slate-700 border-slate-200" },
};

function StatusPill({ status }) {
  const s = STATUSES[status] || STATUSES.em_dia;
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${s.cls}`}>
      <s.icon className="w-3 h-3" /> {s.label}
    </span>
  );
}

export default function CanalDiretoraSecretaria() {
  const { user } = useAuth();
  const [directives, setDirectives] = useState(null);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [contracts, setContracts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ contract_id: "", subject: "", body: "" });

  const isDiretora = user.role === ROLES.DIRETORA;

  const load = async () => {
    const { data } = await api.get("/directives");
    setDirectives(data);
    if (data.length && !active) setActive(data[0]);
    else if (active) {
      const updated = data.find(d => d.id === active.id);
      if (updated) setActive(updated);
    }
  };

  useEffect(() => {
    load();
    api.get("/contracts").then(({ data }) => setContracts(data));
  }, []);

  const createDirective = async () => {
    if (!form.contract_id || !form.subject || !form.body) {
      toast.error("Preencha todos os campos.");
      return;
    }
    try {
      const { data } = await api.post("/directives", form);
      toast.success("Solicitação enviada à Secretária.");
      setOpen(false);
      setForm({ contract_id: "", subject: "", body: "" });
      load();
      setActive(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro.");
    }
  };

  const sendReply = async () => {
    if (!reply.trim() || !active) return;
    try {
      const { data } = await api.post(`/directives/${active.id}/messages`, { body: reply });
      setReply("");
      setActive(data);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro.");
    }
  };

  const setStatus = async (status) => {
    if (!active) return;
    try {
      const { data } = await api.put(`/directives/${active.id}/status`, { status });
      setActive(data);
      load();
    } catch {}
  };

  if (!directives) return <Skeleton className="h-96" />;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-start gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Canal {isDiretora ? "Diretora → Secretária" : "Secretária ↔ Diretora"}
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            {isDiretora
              ? "Solicite providências vinculadas a um contrato e acompanhe o andamento dado pela Secretária."
              : "Responda às solicitações de providência feitas pela Diretora. Registre o andamento na conversa correspondente."}
          </p>
        </div>
        {isDiretora && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-midnight hover:bg-ink text-white" data-testid="new-directive-button">
                <Plus className="w-4 h-4 mr-2" /> Nova solicitação
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Solicitar providência à Secretária</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Contrato</Label>
                  <Select value={form.contract_id} onValueChange={(v) => setForm({ ...form, contract_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                    <SelectContent>
                      {contracts.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.client_name} · {c.product_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Assunto</Label>
                  <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    placeholder="Ex.: Confirmar reunião com Presidente" />
                </div>
                <div>
                  <Label>Detalhes da solicitação</Label>
                  <Textarea rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button onClick={createDirective} className="bg-midnight hover:bg-ink text-white" data-testid="send-directive-button">
                  <Send className="w-4 h-4 mr-2" /> Enviar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Banner explicativo do fluxo */}
      <div className="p-4 border border-blue-200 bg-blue-50/60 rounded-lg flex items-start gap-3" data-testid="canal-explainer">
        <MessageSquare className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-900">
          <strong>Como funciona o Canal:</strong> a Diretora cria uma <em>solicitação de providência</em> vinculada
          a um contrato; isso abre uma conversa aqui no canal. A Secretária responde com o andamento dado, e o
          status (Em dia / Aguardando / Em atraso / Concluída) é gerenciado{" "}
          <strong>somente pela Diretora</strong>. Cada solicitação fica vinculada ao contrato correspondente.
        </div>
      </div>


      <div className="grid lg:grid-cols-12 gap-4 min-h-[60vh]">
        {/* Sidebar list */}
        <Card className="card-soft lg:col-span-4 overflow-hidden">
          <CardHeader>
            <CardTitle className="text-base">Conversas ({directives.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0 max-h-[60vh] overflow-y-auto scroll-thin">
            {directives.length === 0 ? (
              <div className="text-sm text-slate-500 py-8 text-center">Nenhuma solicitação.</div>
            ) : (
              <ul>
                {directives.map(d => (
                  <li key={d.id}>
                    <button onClick={() => setActive(d)} data-testid={`directive-${d.id}`}
                      className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 ${active?.id === d.id ? "bg-slate-100" : ""}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-sm text-slate-900 truncate">{d.subject}</div>
                          <div className="text-xs text-slate-500 mt-0.5 truncate">{d.client_name}</div>
                          <div className="text-xs text-slate-400 mt-1">{formatDateTimeBR(d.updated_at)}</div>
                        </div>
                        <div className="flex-shrink-0">
                          <StatusPill status={d.status} />
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Active thread */}
        <Card className="card-soft lg:col-span-8 flex flex-col">
          {!active ? (
            <CardContent className="flex-1 flex items-center justify-center text-sm text-slate-500">
              Selecione uma conversa.
            </CardContent>
          ) : (
            <>
              <CardHeader className="border-b border-slate-200">
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <div>
                    <CardTitle className="text-base">{active.subject}</CardTitle>
                    <div className="text-xs text-slate-500 mt-1">
                      {active.client_name} · iniciado por {active.created_by_name} em {formatDateTimeBR(active.created_at)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusPill status={active.status} />
                    {isDiretora ? (
                      <Select value={active.status} onValueChange={setStatus}>
                        <SelectTrigger className="h-7 text-xs w-40" data-testid="directive-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(STATUSES).map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-[10px] text-slate-500 italic" data-testid="status-readonly-note">
                        Status gerenciado pela Diretora
                      </span>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="flex-1 overflow-y-auto py-4 space-y-3 scroll-thin">
                {active.messages.map((m) => {
                  const mine = m.author_id === user.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-lg px-3 py-2 ${mine ? "bg-midnight text-white" : "bg-slate-100 text-slate-900"}`}>
                        <div className={`text-[11px] mb-1 ${mine ? "text-slate-300" : "text-slate-500"}`}>
                          {m.author_name} · {m.author_role} · {formatDateTimeBR(m.at)}
                        </div>
                        <div className="text-sm whitespace-pre-line">{m.body}</div>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
              <div className="border-t border-slate-200 p-3 flex gap-2">
                <Textarea rows={2} value={reply} onChange={(e) => setReply(e.target.value)}
                  placeholder="Escreva uma resposta…" className="resize-none"
                  data-testid="directive-reply-input" />
                <Button onClick={sendReply} className="bg-midnight hover:bg-ink text-white" data-testid="send-reply-button">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
